/**
 * §30 — Team activity reporting.
 * Route: /reports/team  (read-only, OrgGuard-protected).
 *
 * Shows:
 *  §30.B  KPI strip (org-level, 4 cards)
 *  §30.C  Team member table with sort, row-hover chip, ⋯ menu
 *  §30.D  Member activity drawer (right-slide)
 *  §30.E  Toolbar filters (date range, role, status, name search)
 *
 * No mutations — editing routes to Settings.
 *
 * Phase 8 · C-47 — The page is always live-data now (reportsTeamService);
 * the old `VITE_REPORTS_TEAM_SOURCE='mock'` fixture path and
 * `mockTeamData.ts` were removed when the drawer moved to
 * teamMemberProfileService (RBAC-gated).
 */

import {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
} from 'react';
import {
  Search,
  Users,
  CheckSquare,
  Mail,
  CalendarCheck,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  ArrowRight,
  Slack,
  Link2,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { KpiCard } from '@modules/dashboard/components/KpiCard';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { Sparkline } from '@shared/components/ui/Sparkline';
import { useAuth } from '@core/auth/useAuth';
import { queryKeys } from '@core/query/queryKeys';
import {
  reportsTeamService,
  type ReportsTeamRange,
  type ReportsTeamRangeKey,
} from '@core/supabase/services/reportsTeamService';
import { MemberActivityDrawer } from './components/MemberActivityDrawer';
import type {
  TeamMember,
  TeamOrgKpis,
  MemberStatus,
  MemberRole,
} from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const LS_DATE_RANGE = 'ggj.reports.team.dateRange';
const LS_FILTERS = 'ggj.reports.team.filters';

const DATE_RANGE_OPTIONS = ['Today', 'Last 7d', 'Last 30d', 'Last 90d', 'Custom'] as const;
type DateRange = (typeof DATE_RANGE_OPTIONS)[number];

const ROLE_OPTIONS: ('All' | MemberRole)[] = ['All', 'Admin', 'Member', 'Viewer'];
const STATUS_OPTIONS: ('All' | MemberStatus)[] = ['All', 'active_now', 'today', 'this_week', 'idle'];
const STATUS_LABEL: Record<MemberStatus, string> = {
  active_now: 'Active now',
  today: 'Today',
  this_week: 'This week',
  idle: 'Idle ≥ 7d',
};

// Sortable numeric column keys
type SortKey =
  | 'activeSearches'
  | 'candidatesAdded'
  | 'shortlisted'
  | 'outreachSent'
  | 'interviewsHeld'
  | 'placements'
  | 'lastActivityAt';

interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

interface Filters {
  role: 'All' | MemberRole;
  status: 'All' | MemberStatus;
  search: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const EMPTY_ORG_KPIS: TeamOrgKpis = {
  activeSearches: 0,
  activeSearchesDelta: '',
  candidatesShortlisted: 0,
  candidatesShortlistedDelta: '',
  outreachSent: 0,
  outreachReplyRate: 0,
  interviewsHeld: 0,
  interviewsShowRate: 0,
};

function serviceMemberToTeamMember(
  m: import('@core/supabase/services/reportsTeamService').ReportsTeamMember,
): TeamMember {
  return {
    id: m.id,
    name: m.name,
    email: m.email,
    role: m.role,
    status: m.status,
    activeSearches: m.activeSearchesCount,
    activeSearchesSpark: m.activeSearchesSpark,
    candidatesAdded: m.candidatesAddedCount,
    shortlisted: m.shortlistedCount,
    outreachSent: m.outreachSentCount,
    outreachReplyRate: m.outreachReplyRate,
    interviewsHeld: m.interviewsHeldCount,
    placements: m.placementsCount,
    // Fall back to epoch so sort/relativeTime stays monotonic for members with no activity.
    lastActivityAt: m.lastActivityAt ?? new Date(0).toISOString(),
    lastActivityRoute: m.lastActivityRoute,
    // Drawer fills these in on open via reportsTeamService.getActivity.
    activeWork: [],
    timelineEvents: [],
  };
}

function serviceKpisToTeamKpis(
  k: import('@core/supabase/services/reportsTeamService').ReportsOrgKpis,
): TeamOrgKpis {
  return {
    activeSearches: k.activeSearches,
    activeSearchesDelta: '',
    candidatesShortlisted: k.candidatesShortlisted,
    candidatesShortlistedDelta: '',
    outreachSent: k.outreachSent,
    outreachReplyRate: k.outreachReplyRate,
    interviewsHeld: k.interviewsHeld,
    interviewsShowRate: k.interviewShowRate,
  };
}

function readLs<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeLs(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* ignore */ }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  const d = Math.floor(diff / 86_400_000);
  return `${d}d ago`;
}

// ---------------------------------------------------------------------------
// StatusChip
// ---------------------------------------------------------------------------
function StatusChip({ status }: { status: MemberStatus }) {
  const classes: Record<MemberStatus, string> = {
    active_now: 'text-emerald-700 bg-emerald-500/15 dark:text-emerald-300',
    today: 'text-emerald-700 bg-emerald-500/15 dark:text-emerald-300',
    this_week: 'text-amber-700 bg-amber-500/15 dark:text-amber-300',
    idle: 'vscode-text-tertiary bg-zinc-200/60 dark:bg-zinc-700/40',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${classes[status]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" aria-hidden />
      {STATUS_LABEL[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// RolePill
// ---------------------------------------------------------------------------
function RolePill({ role }: { role: MemberRole }) {
  const cls =
    role === 'Admin'
      ? 'bg-violet-500/15 text-violet-700 dark:text-violet-300'
      : role === 'Viewer'
        ? 'bg-zinc-500/15 vscode-text-secondary'
        : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${cls}`}>
      {role}
    </span>
  );
}

// ---------------------------------------------------------------------------
// SortIcon
// ---------------------------------------------------------------------------
function SortIcon({ col, sort }: { col: SortKey; sort: SortState }) {
  if (sort.key !== col)
    return <ChevronDown className="h-3 w-3 opacity-30" aria-hidden />;
  return sort.dir === 'asc'
    ? <ChevronUp className="h-3 w-3 text-emerald-500" aria-hidden />
    : <ChevronDown className="h-3 w-3 text-emerald-500" aria-hidden />;
}

// ---------------------------------------------------------------------------
// ContextMenu — ⋯ per row
// ---------------------------------------------------------------------------
function RowMenu({
  member,
  onOpen,
}: {
  member: TeamMember;
  onOpen: (m: TeamMember) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function handleKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'Escape') setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKey}
        className="rounded-[6px] p-1.5 vscode-text-tertiary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-8 z-[30] w-[200px] rounded-[10px] border py-1 shadow-lg vscode-border vscode-bg-elevated"
        >
          <button
            role="menuitem"
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
            onClick={() => { setOpen(false); onOpen(member); }}
          >
            <ArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Open member drawer
          </button>
          <button
            role="menuitem"
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
            onClick={() => {
              setOpen(false);
              window.open(`slack://user?team=T0000&id=${member.id}`, '_blank');
            }}
          >
            <Slack className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Message on Slack
          </button>
          <button
            role="menuitem"
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1.5 text-[13px] vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
            onClick={() => {
              setOpen(false);
              void navigator.clipboard.writeText(
                `${window.location.origin}/reports/team?member=${member.id}`,
              );
            }}
          >
            <Link2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Copy activity link
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MemberRow
// ---------------------------------------------------------------------------
function MemberRow({
  member,
  onOpenDrawer,
}: {
  member: TeamMember;
  onOpenDrawer: (m: TeamMember) => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <tr
      className="group border-b vscode-border transition-colors hover:vscode-bg-hover"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Member */}
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex items-center gap-2">
          <TableAvatar name={member.name} size={20} />
          <span className="text-[13px] font-medium vscode-text-primary">{member.name}</span>
          <RolePill role={member.role} />
        </div>
      </td>

      {/* Status */}
      <td className="whitespace-nowrap px-3 py-2">
        <StatusChip status={member.status} />
      </td>

      {/* Active searches + sparkline */}
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-[13px] tabular-nums vscode-text-primary">
            {member.activeSearches}
          </span>
          <Sparkline values={member.activeSearchesSpark} />
        </div>
      </td>

      {/* Candidates added */}
      <td className="whitespace-nowrap px-3 py-2 text-[13px] tabular-nums vscode-text-primary">
        {member.candidatesAdded}
      </td>

      {/* Shortlisted */}
      <td className="whitespace-nowrap px-3 py-2 text-[13px] tabular-nums vscode-text-primary">
        {member.shortlisted}
      </td>

      {/* Outreach sent */}
      <td className="whitespace-nowrap px-3 py-2 text-[13px] tabular-nums vscode-text-secondary">
        {member.outreachSent}
        {member.outreachSent > 0 && (
          <span className="ml-1 vscode-text-tertiary">· {member.outreachReplyRate}%</span>
        )}
      </td>

      {/* Interviews held */}
      <td className="whitespace-nowrap px-3 py-2 text-[13px] tabular-nums vscode-text-primary">
        {member.interviewsHeld}
      </td>

      {/* Placements — bold emerald when > 0 */}
      <td className="whitespace-nowrap px-3 py-2 text-[13px] tabular-nums">
        <span
          className={member.placements > 0 ? 'font-semibold text-emerald-600 dark:text-emerald-400' : 'vscode-text-primary'}
        >
          {member.placements}
        </span>
      </td>

      {/* Last activity */}
      <td className="whitespace-nowrap px-3 py-2">
        <p className="text-[11px] vscode-text-tertiary">
          {relativeTime(member.lastActivityAt)}
          <span className="mx-1 opacity-50">·</span>
          <span className="font-mono">{member.lastActivityRoute}</span>
        </p>
      </td>

      {/* Actions: ⋯ menu + row-hover [→ View activity] chip (§9.E RowHoverActions) */}
      <td className="whitespace-nowrap px-3 py-2">
        <div className="flex items-center justify-end gap-1">
          {hovered && (
            <button
              type="button"
              onClick={() => onOpenDrawer(member)}
              className="inline-flex h-6 items-center gap-1 rounded-[6px] border px-2 text-[11px] font-medium transition-colors vscode-border vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover"
            >
              → View activity
            </button>
          )}
          <RowMenu member={member} onOpen={onOpenDrawer} />
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// DateRangeDropdown
// ---------------------------------------------------------------------------
function DateRangeDropdown({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (v: DateRange) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-medium vscode-border vscode-text-secondary transition-colors hover:vscode-bg-hover hover:vscode-text-primary"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {value}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-10 z-[30] w-36 rounded-[10px] border py-1 shadow-lg vscode-border vscode-bg-elevated"
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <li
              key={opt}
              role="option"
              aria-selected={opt === value}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`cursor-pointer px-3 py-1.5 text-[13px] transition-colors hover:vscode-bg-hover ${
                opt === value ? 'vscode-text-primary font-medium' : 'vscode-text-secondary'
              }`}
            >
              {opt}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChipSet — filter pill row
// ---------------------------------------------------------------------------
function ChipSet<T extends string>({
  options,
  value,
  labelFn,
  onChange,
}: {
  options: T[];
  value: T;
  labelFn?: (v: T) => string;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={[
            'inline-flex h-7 items-center rounded-full border px-3 text-[12px] font-medium transition-colors',
            opt === value
              ? 'border-[#10B981] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
              : 'vscode-border vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
          ].join(' ')}
          aria-pressed={opt === value}
        >
          {labelFn ? labelFn(opt) : opt}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TeamActivityPage
// ---------------------------------------------------------------------------
// Map the UI's DateRange chip to the service's ReportsTeamRange payload.
function dateRangeToServiceRange(v: DateRange): ReportsTeamRange {
  if (v === 'Today') {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    return { key: 'custom', from: start, to: now.toISOString() };
  }
  if (v === 'Last 7d') return { key: '7d' };
  if (v === 'Last 90d') return { key: '90d' };
  if (v === 'Custom') return { key: 'custom' }; // no UI picker yet → service falls back to 30d
  return { key: '30d' };
}

function serviceRangeKey(v: DateRange): ReportsTeamRangeKey {
  switch (v) {
    case 'Last 7d': return '7d';
    case 'Last 90d': return '90d';
    case 'Custom':
    case 'Today':   return 'custom';
    default:        return '30d';
  }
}

export function TeamActivityPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';

  // Date range
  const [dateRange, setDateRange] = useState<DateRange>(
    () => readLs<DateRange>(LS_DATE_RANGE, 'Last 30d'),
  );
  const onDateRangeChange = useCallback((v: DateRange) => {
    setDateRange(v);
    writeLs(LS_DATE_RANGE, v);
  }, []);

  // Filters
  const [filters, setFilters] = useState<Filters>(
    () => readLs<Filters>(LS_FILTERS, { role: 'All', status: 'All', search: '' }),
  );
  const setFilter = useCallback(<K extends keyof Filters>(key: K, val: Filters[K]) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: val };
      writeLs(LS_FILTERS, next);
      return next;
    });
  }, []);

  // Sort state — default: lastActivityAt desc
  const [sort, setSort] = useState<SortState>({ key: 'lastActivityAt', dir: 'desc' });
  const toggleSort = useCallback((col: SortKey) => {
    setSort((prev) =>
      prev.key === col
        ? { key: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: col, dir: 'desc' },
    );
  }, []);

  // Selected member for drawer
  const [activeMember, setActiveMember] = useState<TeamMember | null>(null);

  // ── Data source (always live now — C-47) ───────────────────────────────
  const rangeKey = serviceRangeKey(dateRange);
  const serviceRange = useMemo(() => dateRangeToServiceRange(dateRange), [dateRange]);

  const membersQuery = useQuery({
    queryKey: queryKeys.reportsTeam.members(orgId, rangeKey, serviceRange.from, serviceRange.to),
    queryFn: async () => {
      const rows = await reportsTeamService.list(orgId, serviceRange);
      return rows.map(serviceMemberToTeamMember);
    },
    enabled: Boolean(orgId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const kpisQuery = useQuery({
    queryKey: queryKeys.reportsTeam.orgKpis(orgId, rangeKey, serviceRange.from, serviceRange.to),
    queryFn: async () => {
      const k = await reportsTeamService.getOrgKpis(orgId, serviceRange);
      return serviceKpisToTeamKpis(k);
    },
    enabled: Boolean(orgId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const members: TeamMember[] = membersQuery.data ?? [];
  const orgKpis: TeamOrgKpis = kpisQuery.data ?? EMPTY_ORG_KPIS;

  const isLoading = membersQuery.isLoading || kpisQuery.isLoading;
  const isError = membersQuery.isError || kpisQuery.isError;

  // Filtered + sorted rows
  const rows = useMemo(() => {
    let list = members;

    if (filters.role !== 'All') {
      list = list.filter((m) => m.role === filters.role);
    }
    if (filters.status !== 'All') {
      list = list.filter((m) => m.status === filters.status);
    }
    if (filters.search.trim()) {
      const q = filters.search.trim().toLowerCase();
      list = list.filter(
        (m) => m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
      );
    }

    return [...list].sort((a, b) => {
      const k = sort.key;
      const av =
        k === 'lastActivityAt'
          ? new Date(a[k] ?? 0).getTime()
          : (a[k] as number);
      const bv =
        k === 'lastActivityAt'
          ? new Date(b[k] ?? 0).getTime()
          : (b[k] as number);
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
  }, [members, filters, sort]);

  const thBase =
    'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider vscode-text-tertiary select-none';
  const thSortable = `${thBase} cursor-pointer hover:vscode-text-primary`;

  function thSort(col: SortKey, label: string) {
    return (
      <th
        scope="col"
        className={thSortable}
        onClick={() => toggleSort(col)}
        aria-sort={
          sort.key === col
            ? sort.dir === 'asc'
              ? 'ascending'
              : 'descending'
            : 'none'
        }
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <SortIcon col={col} sort={sort} />
        </span>
      </th>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-6">
      {/* §30.A — Page header */}
      <PageHeader
        title="Team activity"
        subtitle="Org-wide · last 30 days"
        action={
          <DateRangeDropdown value={dateRange} onChange={onDateRangeChange} />
        }
        breadcrumb={[
          { label: 'Reports', href: '/reports' },
          { label: 'Team activity' },
        ]}
      />

      {/* §30.B — KPI strip */}
      <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4" aria-label="Organisation KPIs">
        <KpiCard
          kpiKey="team-active-searches"
          label="Active searches"
          icon={Search}
          value={orgKpis.activeSearches}
          hint={orgKpis.activeSearchesDelta || undefined}
          enableFeedback
        />
        <KpiCard
          kpiKey="team-shortlisted"
          label="Candidates shortlisted"
          icon={CheckSquare}
          value={orgKpis.candidatesShortlisted}
          hint={orgKpis.candidatesShortlistedDelta || undefined}
          enableFeedback
        />
        <KpiCard
          kpiKey="team-outreach"
          label="Outreach sent"
          icon={Mail}
          value={orgKpis.outreachSent}
          hint={`${orgKpis.outreachReplyRate}% replied`}
          enableFeedback
        />
        <KpiCard
          kpiKey="team-interviews"
          label="Interviews held"
          icon={CalendarCheck}
          value={orgKpis.interviewsHeld}
          hint={`${orgKpis.interviewsShowRate}% showed`}
          enableFeedback
        />
      </section>

      {/* §30.E — Filters toolbar */}
      <div
        className="mb-4 flex flex-wrap items-center gap-3 rounded-[10px] border px-4 py-3 vscode-border vscode-bg-secondary"
        role="search"
        aria-label="Filter team members"
      >
        {/* Name search */}
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 vscode-text-tertiary"
            aria-hidden
          />
          <input
            type="search"
            placeholder="Search name…"
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            className="h-8 w-44 rounded-[8px] border pl-8 pr-3 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[#10B981]"
            aria-label="Search member name"
          />
        </div>

        <span className="h-4 w-px bg-zinc-200 dark:bg-zinc-700" aria-hidden />

        {/* Role chips */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium vscode-text-tertiary">Role</span>
          <ChipSet
            options={ROLE_OPTIONS}
            value={filters.role}
            onChange={(v) => setFilter('role', v)}
          />
        </div>

        <span className="h-4 w-px bg-zinc-200 dark:bg-zinc-700" aria-hidden />

        {/* Status chips */}
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium vscode-text-tertiary">Status</span>
          <ChipSet
            options={STATUS_OPTIONS}
            value={filters.status}
            labelFn={(v) => (v === 'All' ? 'All' : STATUS_LABEL[v as MemberStatus])}
            onChange={(v) => setFilter('status', v)}
          />
        </div>

        {/* Row count */}
        <span className="ml-auto text-[11px] vscode-text-tertiary">
          {rows.length} {rows.length === 1 ? 'member' : 'members'}
        </span>
      </div>

      {/* §30.C — Team member table */}
      <div className="min-w-0 overflow-x-auto rounded-[10px] border vscode-border">
        <table className="w-full min-w-[900px] border-collapse text-sm" role="grid">
          <thead>
            <tr className="border-b vscode-border vscode-bg-secondary">
              <th scope="col" className={thBase}>Member</th>
              <th scope="col" className={thBase}>Status</th>
              {thSort('activeSearches', 'Active searches')}
              {thSort('candidatesAdded', 'Candidates added')}
              {thSort('shortlisted', 'Shortlisted')}
              {thSort('outreachSent', 'Outreach sent')}
              {thSort('interviewsHeld', 'Interviews held')}
              {thSort('placements', 'Placements')}
              {thSort('lastActivityAt', 'Last activity')}
              <th scope="col" className={`${thBase} text-right`} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={10} className="py-10 text-center text-[13px] vscode-text-tertiary">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    Loading team activity…
                  </span>
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={10} className="py-10 text-center text-[13px] text-amber-700 dark:text-amber-300">
                  <span className="inline-flex items-center gap-2">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                    Could not load team activity.
                  </span>
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="py-10 text-center text-[13px] vscode-text-tertiary"
                >
                  No members match the current filters.
                </td>
              </tr>
            ) : (
              rows.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  onOpenDrawer={setActiveMember}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* §30.D — Member activity drawer */}
      <MemberActivityDrawer
        member={activeMember}
        onClose={() => setActiveMember(null)}
      />
    </div>
  );
}
